import React, { createContext, useContext, useState } from 'react';

type Lang = 'de' | 'en';

const translations = {
  de: {
    // Existing
    welcome: 'Willkommen!',
    orderFor: 'Sie bestellen für:',
    table: 'Tisch',
    selectBrand: 'Bitte wählen Sie eine Speisekarte',
    sushiDesc: 'Sushi, Poke Bowls, Maki & mehr',
    pizzaDesc: 'Pizza, Pasta & Insalata',
    cart: 'Warenkorb',
    addToCart: 'In den Warenkorb',
    soldOut: 'AUSVERKAUFT',
    placeOrder: 'Bestellung aufgeben',
    total: 'Gesamt',
    items: 'Artikel',
    notes: 'Anmerkungen zur Bestellung',
    notesPlaceholder: 'z.B. Allergien, besondere Wünsche...',
    itemNotes: 'Anmerkungen',
    itemNotesPlaceholder: 'z.B. ohne Gurke, extra scharf...',
    orderPlaced: 'aufgegeben!',
    orderPreparing: 'Ihre Bestellung wird zubereitet. Bitte warten Sie an Ihrem Tisch.',
    newOrder: 'Neue Bestellung',
    back: 'Zurück',
    quantity: 'Menge',
    extras: 'Extras',
    extrasLoading: 'Extras werden geladen...',
    remove: 'Entfernen',
    emptyCart: 'Ihr Warenkorb ist leer.',
    backToMenu: 'Zur Speisekarte',
    scanQr: 'Bitte scannen Sie den QR-Code an Ihrem Tisch',
    scanQrDesc: 'Um eine Bestellung aufzugeben, scannen Sie den QR-Code, der sich auf Ihrem Tisch befindet.',
    orderNumber: 'Bestellung',
    close: 'Schließen',
    loading: 'Speisekarte wird geladen...',
    error: 'Speisekarte konnte nicht geladen werden.',
    tryAgain: 'Erneut versuchen',
    submitting: 'Wird gesendet...',
    submitError: 'Bestellung konnte nicht aufgegeben werden. Bitte versuchen Sie es erneut.',
    language: 'Sprache',
    searchMenu: 'Speisekarte durchsuchen...',
    noResults: 'Keine Ergebnisse für',
    results: 'Ergebnisse',
    all: 'Alle',

    // Webshop — Landing
    landingTitle: 'Sushi, Pizza & mehr — direkt zu Ihnen!',
    landingSubtitle: 'Bestellen Sie online zur Lieferung oder Abholung.',
    howToOrder: 'Wie möchten Sie bestellen?',
    delivery: 'Lieferung',
    pickup: 'Abholung',
    deliveryDesc: 'Wir liefern direkt zu Ihnen nach Hause',
    pickupDesc: 'Bestellen & vor Ort abholen',
    address: 'Adresse',
    hours: 'Öffnungszeiten',
    openingHours: 'Di–So 11:30–22:00',
    featureFresh: 'Frisch zubereitet',
    featureFree: 'Kostenlose Lieferung',
    featureCash: 'Bar & PayPal',
    landingFooter: 'Sushiki · Sudetenstr. 60 · 50354 Hürth',

    // Webshop — Checkout
    checkout: 'Kasse',
    yourOrder: 'Ihre Bestellung',
    editCart: 'Warenkorb bearbeiten',
    goToCheckout: 'Weiter zur Kasse',
    deliveryAddress: 'Lieferadresse',
    fullName: 'Vollständiger Name',
    street: 'Straße & Hausnummer',
    postcode: 'PLZ',
    city: 'Stadt',
    phone: 'Telefonnummer',
    deliveryNotes: 'Lieferhinweise',
    deliveryNotesPlaceholder: 'z.B. 2. OG links, Hinterhof...',
    pickupDetails: 'Abholdetails',
    pickupTime: 'Abholzeit',
    asap: 'So schnell wie möglich',
    specificTime: 'Bestimmte Uhrzeit',
    paymentMethod: 'Zahlungsmethode',
    cashOnDelivery: 'Barzahlung bei Lieferung',
    cashOnPickup: 'Barzahlung bei Abholung',
    subtotal: 'Zwischensumme',
    deliveryFee: 'Liefergebühr',
    free: 'Kostenlos',
    minOrder: 'Mindestbestellwert',

    // Webshop — Confirmation
    deliveryConfirm: 'Ihre Bestellung wird zubereitet und in Kürze geliefert!',
    pickupConfirm: 'Ihre Bestellung wird zubereitet. Bitte holen Sie sie ab bei:',

    // Auth
    signIn: 'Anmelden',
    signInDesc: 'Melden Sie sich an, um Ihre Bestellung aufzugeben.',
    createAccount: 'Konto erstellen',
    createAccountDesc: 'Erstellen Sie ein Konto, um zu bestellen.',
    email: 'E-Mail',
    password: 'Passwort',
    register: 'Registrieren',
    alreadyAccount: 'Bereits ein Konto?',
    noAccount: 'Noch kein Konto?',
    nameRequired: 'Bitte geben Sie Ihren Namen ein.',
    phoneRequired: 'Bitte geben Sie Ihre Telefonnummer ein.',
    passwordTooShort: 'Passwort muss mindestens 6 Zeichen lang sein.',
    emailInUse: 'Diese E-Mail wird bereits verwendet.',
    invalidEmail: 'Ungültige E-Mail-Adresse.',
    wrongPassword: 'Falsches Passwort.',
    userNotFound: 'Kein Konto mit dieser E-Mail gefunden.',
    tooManyAttempts: 'Zu viele Versuche. Bitte versuchen Sie es später erneut.',
    authError: 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    minSixChars: 'Mind. 6 Zeichen',

    // Account
    myAccount: 'Mein Konto',
    profile: 'Profil',
    edit: 'Bearbeiten',
    save: 'Speichern',
    cancel: 'Abbrechen',
    orderHistory: 'Bestellverlauf',
    noOrders: 'Noch keine Bestellungen.',
    browseMenu: 'Speisekarte ansehen',
    myOrders: 'Meine Bestellungen',
    logout: 'Abmelden',
    statusPending: 'Ausstehend',
    statusConfirmed: 'Bestätigt',
    statusPreparing: 'In Zubereitung',
    statusReady: 'Bereit',
    statusCompleted: 'Abgeschlossen',
    statusDelivered: 'Geliefert',
    statusCancelled: 'Storniert',
  },
  en: {
    // Existing
    welcome: 'Welcome!',
    orderFor: 'You are ordering for:',
    table: 'Table',
    selectBrand: 'Please choose a menu',
    sushiDesc: 'Sushi, Poke Bowls, Maki & more',
    pizzaDesc: 'Pizza, Pasta & Salads',
    cart: 'Cart',
    addToCart: 'Add to Cart',
    soldOut: 'SOLD OUT',
    placeOrder: 'Place Order',
    total: 'Total',
    items: 'items',
    notes: 'Order Notes',
    notesPlaceholder: 'e.g. allergies, special requests...',
    itemNotes: 'Notes',
    itemNotesPlaceholder: 'e.g. no cucumber, extra spicy...',
    orderPlaced: 'placed!',
    orderPreparing: 'Your order is being prepared. Please wait at your table.',
    newOrder: 'New Order',
    back: 'Back',
    quantity: 'Quantity',
    extras: 'Extras',
    extrasLoading: 'Loading extras...',
    remove: 'Remove',
    emptyCart: 'Your cart is empty.',
    backToMenu: 'Go to Menu',
    scanQr: 'Please scan the QR code at your table',
    scanQrDesc: 'To place an order, scan the QR code located on your table.',
    orderNumber: 'Order',
    close: 'Close',
    loading: 'Loading menu...',
    error: 'Could not load menu.',
    tryAgain: 'Try again',
    submitting: 'Submitting...',
    submitError: 'Could not place order. Please try again.',
    language: 'Language',
    searchMenu: 'Search menu...',
    noResults: 'No results for',
    results: 'results',
    all: 'All',

    // Webshop — Landing
    landingTitle: 'Sushi, Pizza & more — delivered to you!',
    landingSubtitle: 'Order online for delivery or pickup.',
    howToOrder: 'How would you like to order?',
    delivery: 'Delivery',
    pickup: 'Pickup',
    deliveryDesc: 'We deliver straight to your door',
    pickupDesc: 'Order ahead & collect in store',
    address: 'Address',
    hours: 'Opening Hours',
    openingHours: 'Tue–Sun 11:30–22:00',
    featureFresh: 'Freshly prepared',
    featureFree: 'Free delivery',
    featureCash: 'Cash & PayPal',
    landingFooter: 'Sushiki · Sudetenstr. 60 · 50354 Hürth',

    // Webshop — Checkout
    checkout: 'Checkout',
    yourOrder: 'Your Order',
    editCart: 'Edit Cart',
    goToCheckout: 'Go to Checkout',
    deliveryAddress: 'Delivery Address',
    fullName: 'Full Name',
    street: 'Street & Number',
    postcode: 'Postcode',
    city: 'City',
    phone: 'Phone Number',
    deliveryNotes: 'Delivery Instructions',
    deliveryNotesPlaceholder: 'e.g. 2nd floor left, back yard...',
    pickupDetails: 'Pickup Details',
    pickupTime: 'Pickup Time',
    asap: 'As soon as possible',
    specificTime: 'Specific time',
    paymentMethod: 'Payment Method',
    cashOnDelivery: 'Cash on Delivery',
    cashOnPickup: 'Cash on Pickup',
    subtotal: 'Subtotal',
    deliveryFee: 'Delivery Fee',
    free: 'Free',
    minOrder: 'Minimum order',

    // Webshop — Confirmation
    deliveryConfirm: 'Your order is being prepared and will be delivered shortly!',
    pickupConfirm: 'Your order is being prepared. Please pick it up at:',

    // Auth
    signIn: 'Sign In',
    signInDesc: 'Sign in to place your order.',
    createAccount: 'Create Account',
    createAccountDesc: 'Create an account to start ordering.',
    email: 'Email',
    password: 'Password',
    register: 'Register',
    alreadyAccount: 'Already have an account?',
    noAccount: "Don't have an account?",
    nameRequired: 'Please enter your name.',
    phoneRequired: 'Please enter your phone number.',
    passwordTooShort: 'Password must be at least 6 characters.',
    emailInUse: 'This email is already in use.',
    invalidEmail: 'Invalid email address.',
    wrongPassword: 'Incorrect password.',
    userNotFound: 'No account found with this email.',
    tooManyAttempts: 'Too many attempts. Please try again later.',
    authError: 'Authentication failed. Please try again.',
    minSixChars: 'Min. 6 characters',

    // Account
    myAccount: 'My Account',
    profile: 'Profile',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    orderHistory: 'Order History',
    noOrders: 'No orders yet.',
    browseMenu: 'Browse Menu',
    myOrders: 'My Orders',
    logout: 'Sign Out',
    statusPending: 'Pending',
    statusConfirmed: 'Confirmed',
    statusPreparing: 'Preparing',
    statusReady: 'Ready',
    statusCompleted: 'Completed',
    statusDelivered: 'Delivered',
    statusCancelled: 'Cancelled',
  },
};

interface LanguageContextType {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  lang: 'de',
  setLang: () => {},
  t: (key) => key,
});

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLang] = useState<Lang>(() => {
    return (localStorage.getItem('qr_lang') as Lang) || 'de';
  });

  const changeLang = (l: Lang) => {
    setLang(l);
    localStorage.setItem('qr_lang', l);
  };

  const t = (key: string): string => {
    return (translations[lang] as any)[key] || (translations['de'] as any)[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang: changeLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
